import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { first } from 'lodash';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import WorkQueues from 'enums/workQueue.enum';
import ManageTagsDialog from 'modules/PatientView/components/EligibilityActionItems/ManageTagsDialog';
import { workQueueActions } from 'store/slices/workQueueSlice';
import { upsertLineItemTags } from 'store/thunks/tagThunks';
import { applyUrgentTag, removeUrgentTag } from 'store/thunks/workQueueThunks';

const tagShape = PropTypes.shape({
  tagName: PropTypes.string,
  isSystemTag: PropTypes.bool,
});

const OrderVerificationOptions = ({ order, currentPage }) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);
  const menuOpen = !!anchorEl;

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleManageTagsClick = () => {
    handleMenuClose();
    setIsTagsDialogOpen(true);
  };

  const handleTagsDialogClose = () => {
    setIsTagsDialogOpen(false);
  };

  const handleSaveTags = async ({ lineItemId, tags: newTags, urgentShipByDate }) => {
    const currentTags = order.tags || [];
    const currentTagNames = currentTags.map((t) => t.tagName);
    const newTagNames = (newTags || []).map((t) => t.tagName);
    const urgentAdded = newTagNames.includes('URGENT') && !currentTagNames.includes('URGENT');
    const urgentRemoved = !newTagNames.includes('URGENT') && currentTagNames.includes('URGENT');
    const urgentReAdded =
      Boolean(urgentShipByDate) &&
      newTagNames.includes('URGENT') &&
      currentTagNames.includes('URGENT');

    if (!urgentAdded && !urgentRemoved && !urgentReAdded) {
      return dispatch(upsertLineItemTags({ lineItemId, currentTags, newTags })).then((response) => {
        const updatedTags = first(response?.updatedLineItemTags)?.tags;
        dispatch(
          workQueueActions.updateLineItemTags({
            preset: WorkQueues.ORDER_VERIFICATION,
            page: currentPage,
            lineItemId,
            tags: updatedTags,
          })
        );
        handleTagsDialogClose();
        return response;
      });
    }

    // URGENT tag was added, removed, or re-added with a new ship by date
    let urgentResult;
    if (urgentAdded || urgentReAdded) {
      urgentResult = await dispatch(
        applyUrgentTag({
          lineItemId,
          mpi: order.mpi,
          orderId: order.orderId,
          shipByDate: urgentShipByDate,
        })
      );
    } else {
      urgentResult = await dispatch(
        removeUrgentTag({ lineItemId, mpi: order.mpi, orderId: order.orderId })
      );
    }

    // Also handle any concurrent non-URGENT tag changes
    const nonUrgentCurrentTags = currentTags.filter((t) => t.tagName !== 'URGENT');
    const nonUrgentNewTags = (newTags || []).filter((t) => t.tagName !== 'URGENT');
    const nonUrgentCurrentNames = nonUrgentCurrentTags
      .map((t) => t.tagName)
      .sort()
      .join(',');
    const nonUrgentNewNames = nonUrgentNewTags
      .map((t) => t.tagName)
      .sort()
      .join(',');
    let updatedTags = urgentResult?.tags;
    if (nonUrgentCurrentNames !== nonUrgentNewNames) {
      const tagResponse = await dispatch(
        upsertLineItemTags({
          lineItemId,
          currentTags: nonUrgentCurrentTags,
          newTags: nonUrgentNewTags,
        })
      );
      updatedTags = first(tagResponse?.updatedLineItemTags)?.tags ?? updatedTags;
    }

    dispatch(
      workQueueActions.updateLineItemTags({
        preset: WorkQueues.ORDER_VERIFICATION,
        page: currentPage,
        lineItemId,
        tags: updatedTags,
      })
    );
    handleTagsDialogClose();
    return { updatedLineItemTags: [{ lineItemId, tags: updatedTags }] };
  };

  return (
    <>
      <IconButton
        id={`OrderVerification-${order.lineItemId}-Options`}
        aria-label={`Order Verification Options ${order.lineItemId}`}
        onClick={handleMenuOpen}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id={`OrderVerification-${order.lineItemId}-options-menu`}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleManageTagsClick}>Manage Tags</MenuItem>
      </Menu>

      {isTagsDialogOpen && (
        <ManageTagsDialog
          open={isTagsDialogOpen}
          onClose={handleTagsDialogClose}
          mpi={order.mpi}
          orderId={order.orderId}
          lineItemId={order.lineItemId}
          existingTags={order.tags || []}
          onSave={handleSaveTags}
          patientDetails={{ patientName: order.patientName }}
        />
      )}
    </>
  );
};

OrderVerificationOptions.propTypes = {
  order: PropTypes.shape({
    mpi: PropTypes.string.isRequired,
    orderId: PropTypes.string.isRequired,
    lineItemId: PropTypes.string.isRequired,
    patientName: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(tagShape),
  }).isRequired,
  currentPage: PropTypes.number.isRequired,
};

export default OrderVerificationOptions;
